import React, { createContext, useContext, useMemo, useEffect, useRef } from 'react';
import Spinners from '../components/spinner';
import useWealthlaneIdentityApi from '../hooks/useWealthlaneIdentityApi';
import useGetCurrentCompanyBrandingConfig from '../hooks/get/useGetCurrentCompanyBrandingConfig';

interface BrandingAsset {
  type?: string;
  url?: string;
  is_external?: boolean;
}

export interface BrandingConfig {
  logo?: BrandingAsset;
  favicon?: BrandingAsset;
  pageTitlePrefix?: string | null;
}

// Raw config shape from API (kebab-case keys)
interface RawBrandingConfig {
  logo?: BrandingAsset;
  'fav-icon'?: BrandingAsset;
  'page-title-prefix'?: string | null;
}

interface BrandingContextValue {
  config: BrandingConfig | null;
  appLogoUrl: string | null;
  appFaviconUrl: string | null;
  pageTitlePrefix: string | null;
  isLoading: boolean;
  /** Whether branding is enabled (has auth context to fetch branding) */
  isEnabled: boolean;
  /** Whether branding config has been resolved (loaded or determined empty) */
  isResolved: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  config: null,
  appLogoUrl: null,
  appFaviconUrl: null,
  pageTitlePrefix: null,
  isLoading: false,
  isEnabled: false,
  isResolved: false,
});

function normalizeInternalUrl(base: string, pathOrUrl: string): string {
  // If already absolute, keep it.
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const cleanedBase = (base ?? '').replace(/\/$/, '');
  const cleanedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${cleanedBase}${cleanedPath}`;
}

/**
 * Normalize kebab-case keys from API to camelCase
 */
function normalizeConfig(raw: RawBrandingConfig): BrandingConfig {
  return {
    logo: raw.logo,
    favicon: raw['fav-icon'],
    pageTitlePrefix: raw['page-title-prefix'] ?? null,
  };
}

/**
 * Build full URL for an asset
 */
function buildAssetUrl(asset: BrandingAsset | undefined): string | null {
  if (!asset?.url) return null;
  if (asset.is_external) return asset.url;
  return normalizeInternalUrl(process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '', asset.url);
}

// Fallback title from environment variable (e.g., "Wealthlane Participant")
const FALLBACK_TITLE = process.env.REACT_APP_SITE_TITLE ?? 'Wealthlane Participant';
// Fallback favicon path (relative to public folder)
const FALLBACK_FAVICON = '/favicon.ico';

export const BrandingProvider = ({
  children,
  enabled,
}: {
  children: React.ReactNode;
  enabled: boolean;
}): JSX.Element => {
  const { handleRequest } = useWealthlaneIdentityApi();
  const { data, isLoading } = useGetCurrentCompanyBrandingConfig(handleRequest, enabled);
  
  // Track if title has been set (to avoid flicker)
  const titleSetRef = useRef<boolean>(false);
  // Track the current favicon URL to avoid unnecessary updates
  const currentFaviconUrlRef = useRef<string | null>(null);

  const parsed = useMemo((): BrandingConfig | null => {
    if (!enabled) return null;
    if (data == null || data === '') return null;
    try {
      const raw = JSON.parse(data) as RawBrandingConfig;
      return normalizeConfig(raw);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error parsing branding config:', e);
      return null;
    }
  }, [data, enabled]);

  const appLogoUrl = useMemo((): string | null => {
    return buildAssetUrl(parsed?.logo);
  }, [parsed]);

  const appFaviconUrl = useMemo((): string | null => {
    return buildAssetUrl(parsed?.favicon);
  }, [parsed]);

  const pageTitlePrefix = useMemo((): string | null => {
    return parsed?.pageTitlePrefix ?? null;
  }, [parsed]);

  // Track if branding config has been fetched (resolved with data or empty)
  const configResolved = !isLoading;

  // Effect: Update favicon in DOM after branding config resolves
  // Flow: blank → wait for config → apply branding OR fallback
  useEffect(() => {
    // If branding is enabled, wait for config to resolve before doing anything
    if (enabled && !configResolved) return;

    // Config has resolved (or branding is disabled) - now decide what to show
    const baseFaviconUrl = appFaviconUrl ?? FALLBACK_FAVICON;

    // Only update if the favicon URL has actually changed
    if (currentFaviconUrlRef.current === baseFaviconUrl) return;
    currentFaviconUrlRef.current = baseFaviconUrl;

    // Add cache-busting query param only on first load to prevent browser from showing cached favicon
    const faviconUrl = `${baseFaviconUrl}${baseFaviconUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;

    // Remove existing favicon links first to prevent cached version from showing
    const existingIcons = document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]');
    existingIcons.forEach((icon) => icon.remove());

    // Create new favicon links
    const createFaviconLink = (rel: string) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = faviconUrl;
      document.head.appendChild(link);
    };

    createFaviconLink('icon');
    createFaviconLink('shortcut icon');
  }, [enabled, configResolved, appFaviconUrl]);

  // Effect: Update document title after branding config resolves
  // Flow: blank → wait for config → apply branding OR fallback
  useEffect(() => {
    // If branding is enabled, wait for config to resolve before doing anything
    if (enabled && !configResolved) return;

    // Config has resolved (or branding is disabled) - now decide what to show
    if (pageTitlePrefix) {
      // Apply branding: "{prefix} Participant" format
      // Extract the app type from fallback (e.g., "Participant" from "Wealthlane Participant")
      const appType = FALLBACK_TITLE.replace(/^Wealthlane\s*/i, '');
      document.title = `${pageTitlePrefix} ${appType}`.trim();
    } else {
      // No branding prefix - use fallback title
      document.title = FALLBACK_TITLE;
    }
    titleSetRef.current = true;
  }, [enabled, configResolved, pageTitlePrefix]);

  // Block app rendering until branding is loaded (only when enabled)
  if (enabled && isLoading) {
    return <Spinners containerClassName='h-screen' />;
  }

  return (
    <BrandingContext.Provider
      value={{
        config: parsed,
        appLogoUrl,
        appFaviconUrl,
        pageTitlePrefix,
        isLoading: enabled ? isLoading : false,
        isEnabled: enabled,
        isResolved: !enabled || configResolved,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextValue => useContext(BrandingContext);
